// src/pages/Login.jsx
import React, { useRef, useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

import logoLight from '../assets/lumochart-login.png';
import logoDark from '../assets/lumochart-login-dark.png';
import googleLogo from '../assets/google-logo.svg';

const inputClasses =
  "w-full mt-1 px-4 py-3 bg-slate-100 dark:bg-zinc-800 border-none rounded-lg text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors";

export default function Login() {
  const [activeTab, setActiveTab] = useState('login');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login, signup, signInWithGoogle, signInWithApple } = useAuth();

  const loginEmailRef = useRef();
  const loginPasswordRef = useRef();
  const signupNameRef = useRef();
  const signupEmailRef = useRef();
  const signupPasswordRef = useRef();

  // 🌙 Persistent Dark Mode — syncs with localStorage and global index.js
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    if (saved !== null) return JSON.parse(saved);
    return (
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
    );
  });

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.toggle('dark', darkMode);
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  const handleAuthAction = async (e, action) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (action === 'login') {
        await login(
          loginEmailRef.current.value,
          loginPasswordRef.current.value
        );
      } else if (action === 'signup') {
        if (signupPasswordRef.current.value.length < 6) {
          throw new Error('Password must be at least 6 characters long.');
        }
        await signup(
          signupEmailRef.current.value,
          signupPasswordRef.current.value
        );
      }
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'An unknown error occurred.');
    }
    setLoading(false);
  };

  const handleSocialLogin = async (provider) => {
    try {
      setError('');
      setLoading(true);
      if (provider === 'google') await signInWithGoogle();
      if (provider === 'apple') await signInWithApple();
      navigate('/dashboard');
    } catch {
      setError(
        `${provider.charAt(0).toUpperCase() + provider.slice(1)} sign-in failed.`
      );
    }
    setLoading(false);
  };

  const formVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  };

  return (
    <div className="bg-[#f4f7f9] dark:bg-black min-h-screen flex items-center justify-center p-6 transition-colors duration-300">
      <div className="w-full max-w-md bg-white dark:bg-zinc-900 rounded-2xl shadow-xl p-8 space-y-6">
        <div className="flex items-center justify-between">
          {/* 🧭 Logo now links to /lumochart, not / */}
          <Link to="/lumochart" title="Back to LumoChart Home">
            <img
              src={darkMode ? logoDark : logoLight}
              alt="LumoChart Logo"
              className="h-10 w-auto"
            />
          </Link>

          <button
            onClick={() => setDarkMode(!darkMode)}
            className="text-sm bg-slate-100 dark:bg-zinc-800 text-gray-600 dark:text-white px-3 py-1 rounded-full hover:scale-105 transition"
          >
            {darkMode ? '☀️ Light' : '🌙 Dark'}
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-3 rounded-md text-sm">
            {error}
          </div>
        )}

        {/* Login / Signup Tabs */}
        <div className="flex items-center space-x-2 bg-slate-200/70 dark:bg-zinc-800 rounded-full p-1">
          {['login', 'signup'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className="relative flex-1 px-3 py-1.5 text-sm font-semibold transition-colors duration-300 capitalize"
            >
              <span
                className={`relative z-10 ${
                  activeTab === tab
                    ? 'text-gray-800 dark:text-white'
                    : 'text-gray-500 hover:text-gray-800 dark:hover:text-white'
                }`}
              >
                {tab === 'login' ? 'Log In' : 'Sign Up'}
              </span>
              {activeTab === tab && (
                <motion.div
                  layoutId="active-pill-login"
                  className="absolute inset-0 bg-white dark:bg-zinc-700 rounded-full shadow-sm"
                  transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                />
              )}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            variants={formVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.2 }}
          >
            {activeTab === 'login' ? (
              <form
                onSubmit={(e) => handleAuthAction(e, 'login')}
                className="space-y-4"
              >
                <input
                  type="email"
                  ref={loginEmailRef}
                  required
                  placeholder="Email Address"
                  className={inputClasses}
                />
                <input
                  type="password"
                  ref={loginPasswordRef}
                  required
                  placeholder="Password"
                  className={inputClasses}
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-500 text-white py-3 rounded-lg font-semibold hover:bg-blue-600 transition"
                >
                  {loading ? 'Logging In...' : 'Log In'}
                </button>
              </form>
            ) : (
              <form
                onSubmit={(e) => handleAuthAction(e, 'signup')}
                className="space-y-4"
              >
                <input
                  type="text"
                  ref={signupNameRef}
                  required
                  placeholder="Full Name"
                  className={inputClasses}
                />
                <input
                  type="email"
                  ref={signupEmailRef}
                  required
                  placeholder="Email Address"
                  className={inputClasses}
                />
                <input
                  type="password"
                  ref={signupPasswordRef}
                  required
                  placeholder="Password"
                  className={inputClasses}
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-500 text-white py-3 rounded-lg font-semibold hover:bg-blue-600 transition"
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </button>
              </form>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="relative flex items-center py-2">
          <div className="flex-grow border-t border-gray-200 dark:border-zinc-700"></div>
          <span className="flex-shrink mx-4 text-xs text-gray-400">OR</span>
          <div className="flex-grow border-t border-gray-200 dark:border-zinc-700"></div>
        </div>

        {/* Social logins */}
        <div className="space-y-3">
          <button
            onClick={() => handleSocialLogin('google')}
            className="w-full flex items-center justify-center gap-3 border border-gray-300 dark:border-zinc-700 py-2.5 rounded-lg bg-white dark:bg-zinc-800/50 hover:bg-slate-50 dark:hover:bg-zinc-800 transition"
          >
            <img src={googleLogo} alt="Google" className="h-5 w-5" />
            <span className="text-sm font-medium text-gray-700 dark:text-white">
              Continue with Google
            </span>
          </button>

          <button
            onClick={() => handleSocialLogin('apple')}
            className="w-full flex items-center justify-center gap-3 border border-gray-300 dark:border-zinc-700 py-2.5 rounded-lg bg-white dark:bg-zinc-800/50 hover:bg-slate-50 dark:hover:bg-zinc-800 transition"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 384 512"
              className="h-5 w-5 fill-black dark:fill-white transition"
            >
              <path d="M318.7 268.7c-.2-50.1 41.1-74.1 42.9-75.2-23.4-34.2-59.7-38.9-72.6-39.4-30.9-3.1-60.3 18.1-75.9 18.1s-39.8-17.6-65.3-17.1c-33.6 .5-64.6 19.6-81.9 49.7-35 60.7-8.9 150.8 25.1 200.2 16.6 24.1 36.3 51.1 62.2 50 24.6-1 33.9-16.1 63.4-16.1s37.5 16.1 63.2 15.6c26.1-.5 42.7-24.5 58.7-48.8 18.3-26.7 25.9-52.6 26.2-53.9-.6-.3-50.3-19.4-50.5-76.9zm-49.1-149.9c13.9-16.9 23.2-40.4 20.6-64.1-19.9 .8-43.9 13.2-58.1 30.1-12.8 14.8-24 38.6-21 61.3 22.2 1.7 44.7-11.3 58.5-27.3z" />
            </svg>
            <span className="text-sm font-medium text-gray-700 dark:text-white">
              Continue with Apple
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
